/** @file Count-preserving triangle edits on selected vertices: flip a shared edge, add or remove a triangle. */
import { flatOrient } from '@pose2d/autorig/mesh/flatOrient';

type Tri = [number, number, number];

const triAt = (tris: readonly number[], t: number): Tri => [tris[t] ?? 0, tris[t + 1] ?? 0, tris[t + 2] ?? 0];
const sameSet = (t: Tri, a: number, b: number, c: number): boolean => t.includes(a) && t.includes(b) && t.includes(c);

/** Offsets of the triangles holding edge {a, b}, with the edge in each one's winding (u → w) and the third corner. */
function sides(tris: readonly number[], a: number, b: number): { t: number; u: number; w: number; c: number }[] {
  const out: { t: number; u: number; w: number; c: number }[] = [];
  for (let t = 0; t < tris.length; t += 3) {
    const tri = triAt(tris, t);
    const k = tri.findIndex((v, i) => { const w = tri[(i + 1) % 3]; return (v === a && w === b) || (v === b && w === a); });
    if (k >= 0) out.push({ t, u: tri[k] ?? 0, w: tri[(k + 1) % 3] ?? 0, c: tri[(k + 2) % 3] ?? 0 });
  }
  return out;
}

/** New triangle lists (rest positions `rest`, 2 per vertex, y-up), or null when the edit does not apply. */
export const triangleEdits = {
  /** Replaces the diagonal a–b of the two triangles sharing it by c–d; only when the quad is convex (proper crossing). */
  flip(tris: readonly number[], rest: readonly number[], a: number, b: number): number[] | null {
    const [s1, s2, extra] = sides(tris, a, b);
    if (s1 === undefined || s2 === undefined || extra !== undefined) return null;
    const crosses = flatOrient(rest, a, b, s1.c) * flatOrient(rest, a, b, s2.c) < 0 && flatOrient(rest, s1.c, s2.c, a) * flatOrient(rest, s1.c, s2.c, b) < 0;
    if (!crosses) return null;
    const out = [...tris];
    out.splice(s1.t, 3, s1.u, s2.c, s1.c);
    out.splice(s2.t, 3, s2.c, s1.w, s1.c);
    return out;
  },
  /** Appends triangle (a, b, c), counter-clockwise in rest space; null for repeated, out-of-range or existing ones. */
  add(tris: readonly number[], rest: readonly number[], a: number, b: number, c: number): number[] | null {
    const count = rest.length / 2;
    if (new Set([a, b, c]).size < 3 || [a, b, c].some((v) => !(v >= 0 && v < count))) return null;
    for (let t = 0; t < tris.length; t += 3) if (sameSet(triAt(tris, t), a, b, c)) return null;
    return flatOrient(rest, a, b, c) >= 0 ? [...tris, a, b, c] : [...tris, a, c, b];
  },
  /** Removes the triangle with exactly these corners; null when there is none or it is the last one. */
  remove(tris: readonly number[], a: number, b: number, c: number): number[] | null {
    for (let t = 0; t < tris.length; t += 3) {
      if (sameSet(triAt(tris, t), a, b, c)) return tris.length > 3 ? [...tris.slice(0, t), ...tris.slice(t + 3)] : null;
    }
    return null;
  },
};
